/**
 * Whether this app has a connection: what the browser says, corrected by what
 * the API itself answered (G8 of the ledger live test).
 *
 * `navigator.onLine` alone is not enough. Chrome on a machine that was online
 * reported `false` while every request to the API answered 200, and the app
 * drew "This device is offline" and OS's "needs the connection" over a page
 * that was working. So an answer from the API wins over the browser's word:
 *
 * - The browser says online: the app is online. A server that does not answer
 *   while the device is online is the sync flow's "Not syncing", not this.
 * - The browser says offline, and the last request to the API was answered:
 *   the app is online all the same.
 * - The browser says offline, and the last request failed to reach the API,
 *   or none has been made since the browser went offline: the app is offline.
 *
 * Any answer counts, a refusal included: a 4xx or a 5xx still came back over
 * the network. Only a request that never reached a server counts as a failure.
 *
 * The decision is pure (`isOnlineNow`, `nextOnline`), so a test can hold it;
 * the store beside it is the wiring `platform/fetch.ts` and `useOnline` share.
 */

/** What the API's requests last said: answered, failed to reach it, or nothing since the browser last went offline. */
export type ApiReach = 'answered' | 'unreachable' | 'unknown';

export type OnlineState = { readonly browser: boolean; readonly api: ApiReach };

export type OnlineEvent =
  | { readonly type: 'browser'; readonly online: boolean }
  | { readonly type: 'api_answered' }
  | { readonly type: 'api_unreachable' };

/** Online when the browser says so, or when the API answered since it last said otherwise. */
export const isOnlineNow = (state: OnlineState): boolean => state.browser || state.api === 'answered';

/**
 * One thing that happened to the connection. The browser going offline forgets
 * the API's last answer, so a real drop shows at once; the next answer brings
 * the app back however long the browser goes on saying offline.
 */
export const nextOnline = (state: OnlineState, event: OnlineEvent): OnlineState => {
  switch (event.type) {
    case 'browser':
      if (event.online) return state.browser ? state : { ...state, browser: true };
      return { browser: false, api: 'unknown' };
    case 'api_answered':
      return state.api === 'answered' ? state : { ...state, api: 'answered' };
    case 'api_unreachable':
      return state.api === 'unreachable' ? state : { ...state, api: 'unreachable' };
  }
};

const browserSays = (): boolean => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);

let state: OnlineState = { browser: browserSays(), api: 'unknown' };
const listeners = new Set<() => void>();

const apply = (event: OnlineEvent): void => {
  const next = nextOnline(state, event);
  if (next === state) return;
  const was = isOnlineNow(state);
  state = next;
  if (isOnlineNow(next) !== was) for (const listener of listeners) listener();
};

/** Told by `platform/fetch.ts`: a request to the API was answered, or never reached it. */
export const noteApiAnswered = (): void => apply({ type: 'api_answered' });
export const noteApiUnreachable = (): void => apply({ type: 'api_unreachable' });

/** Whether the app is online now. */
export const appOnline = (): boolean => isOnlineNow(state);

/** Told when `appOnline()` changes; for `useSyncExternalStore`. */
export const subscribeOnline = (onChange: () => void): (() => void) => {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
};

// The browser's word is heard from the start, whoever is listening.
if (typeof window !== 'undefined') {
  const onBrowser = (): void => apply({ type: 'browser', online: browserSays() });
  window.addEventListener('online', onBrowser);
  window.addEventListener('offline', onBrowser);
}
